/**
 * PaymentConfirmation.js
 *
 * Payment confirmation page shown after successful PayPal/Venmo checkout.
 * Displays gift details and a notification that the recipient will be contacted.
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import { SERVER_URL } from './config';

function PaymentConfirmation() {
  const location = useLocation();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // PayPal flow passes state; Stripe flow passes ?session_id in URL
  const paypalState = location.state || {};
  const fundingSource = paypalState.fundingSource;
  const buyerEmail = paypalState.buyerEmail;

  useEffect(() => {
    const giftId = paypalState.giftId;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const id = giftId || sessionId;

    if (id) {
      fetch(`${SERVER_URL}/paypal/gift/${id}`)
        .then(res => res.ok ? res.json() : fetch(`${SERVER_URL}/gift/${id}`).then(r => r.json()))
        .then(data => {
          setGift(data);
          setLoading(false);
        })
        .catch(() => { setError('Failed to load gift details.'); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-md mx-auto mt-16 text-center">
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-md mx-auto mt-10 px-4">
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg text-sm">{error}</div>
        </div>
      </div>
    );
  }

  const recipientName = gift?.recipient_name || 'your recipient';

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeUp 0.45s ease forwards; opacity: 0; }
        .delay-1 { animation-delay: 0.05s; }
        .delay-2 { animation-delay: 0.18s; }
        .delay-3 { animation-delay: 0.32s; }
      `}</style>

      <div className="max-w-md mx-auto px-4 pt-10 pb-16">

        {/* Hero */}
        <div className="text-center mb-8 fade-up">
          <div className="text-5xl mb-4">🎁</div>
          <h1 className="font-display text-3xl font-bold text-gray-900 mb-2">
            Gift sent!
          </h1>
          <p className="text-gray-500 text-sm">
            {recipientName} will be notified about this gift.
          </p>
        </div>

        {/* Receipt card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 p-6 fade-up delay-1">

          {/* Payment method badge */}
          {fundingSource && (
            <div className="mb-5">
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${
                fundingSource === 'venmo'
                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                  : 'bg-sky-50 text-sky-700 border-sky-200'
              }`}>
                {fundingSource === 'venmo' ? '💜 Paid with Venmo' : '💙 Paid with PayPal'}
                {buyerEmail && (
                  <span className="font-normal opacity-70">· {buyerEmail}</span>
                )}
              </span>
            </div>
          )}

          {gift ? (
            <div className="space-y-3">
              <div className="flex justify-between items-baseline">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">To</span>
                <span className="text-sm font-medium text-gray-800">{gift.recipient_name}</span>
              </div>

              <div className="flex justify-between items-baseline">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Amount</span>
                <span className="text-sm font-semibold text-indigo-600">${parseFloat(gift.amount).toFixed(2)}</span>
              </div>
              {gift.occasion && gift.occasion !== 'Just Because' && (
                <div className="flex justify-between items-baseline">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Occasion</span>
                  <span className="text-sm text-gray-600">{gift.occasion}</span>
                </div>
              )}
              {gift.message && (
                <div className="pt-3 mt-1 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Message</p>
                  <p className="text-sm text-gray-700 italic">"{gift.message}"</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-600">Your payment was processed successfully.</p>
          )}
        </div>

        {/* CTAs */}
        <div className="mt-5 flex flex-col sm:flex-row gap-3 fade-up delay-2">
          <a
            href="/send"
            className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold text-center py-2.5 px-4 rounded-lg transition-colors"
          >
            Send another gift
          </a>
          <a
            href="/"
            className="flex-1 border border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-800 text-sm font-medium text-center py-2.5 px-4 rounded-lg transition-colors"
          >
            View feed
          </a>
        </div>

      </div>
    </div>
  );
}

export default PaymentConfirmation;
